import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';

interface Profile {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  position: string | null;
  profile_color: string | null;
  weather_location: string | null;
  show_avatar: boolean;
  subscription_plan: string | null;
  max_team_members: number | null;
  company_name: string | null;
  support_access_enabled: boolean;
  support_access_expires_at: string | null;
  support_access_enabled_at: string | null;
  created_at: string;
  updated_at: string;
}

interface UseProfileReturn {
  profile: Profile | null;
  loading: boolean;
  error: Error | null;
  updateProfile: (updates: Partial<Pick<Profile, 'full_name' | 'avatar_url' | 'position' | 'profile_color' | 'weather_location' | 'show_avatar' | 'company_name' | 'support_access_enabled' | 'support_access_expires_at' | 'support_access_enabled_at'>>) => Promise<void>;
  uploadAvatar: (file: File) => Promise<string>;
  refetch: () => Promise<void>;
}

export const useProfile = (): UseProfileReturn => {
  const { user, loading: authLoading } = useAuthContext();
  const queryClient = useQueryClient();

  const { data: profile = null, isLoading, error: queryError, refetch: queryRefetch } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user!.id)
        .maybeSingle();

      if (error) throw error;
      return data as Profile | null;
    },
    enabled: !!user && !authLoading,
  });

  const uploadAvatar = async (file: File): Promise<string> => {
    if (!user) throw new Error('No user logged in');

    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/avatar.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('files')
      .upload(fileName, file, { upsert: true });

    if (uploadError) throw uploadError;

    const { data, error: signError } = await supabase.storage
      .from('files')
      .createSignedUrl(fileName, 60 * 60 * 24 * 365);

    if (signError || !data?.signedUrl) throw signError || new Error('Failed to create signed URL');

    return data.signedUrl;
  };

  const updateProfile = async (updates: Partial<Pick<Profile, 'full_name' | 'avatar_url' | 'position' | 'profile_color' | 'weather_location' | 'show_avatar' | 'company_name' | 'support_access_enabled' | 'support_access_expires_at' | 'support_access_enabled_at'>>) => {
    if (!user) throw new Error('No user logged in');

    const { error: updateError } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);

    if (updateError) throw updateError;

    queryClient.invalidateQueries({ queryKey: ['profile', user.id] });
    window.dispatchEvent(new Event('profile-updated'));
  };

  const refetch = async () => {
    await queryRefetch();
  };

  return {
    profile,
    loading: isLoading || authLoading,
    error: queryError as Error | null,
    updateProfile,
    uploadAvatar,
    refetch,
  };
};
